import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowRight, Clock, MessageCircle, Sparkles, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import API from "../lib/api";
import { SURVEY_CLOSED } from "../config/surveyConfig";

const DRAFT_KEY = "survey_draft";
const USER_KEY = "survey_user";
const SESSION_KEY = "survey_session";

export default function LandingPage() {
  const [screen, setScreen] = useState("intro");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [hasSavedProgress, setHasSavedProgress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedUser = localStorage.getItem(USER_KEY);
    const savedDraft = localStorage.getItem(DRAFT_KEY);
    if (savedUser && savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (Object.keys(parsed).length > 0) {
          setHasSavedProgress(true);
          const user = JSON.parse(savedUser);
          setName(user.short_name || "");
          setEmail(user.email || "");
        }
      } catch {}
    }
  }, []);

  const handleStartClick = async () => {
    setIsLoading(true);
    try {
      const { data } = await API.post("/survey/start", { email: "" });
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        session_id: data.session_id,
        started_at: data.started_at
      }));
      setScreen("info");
    } catch (e) {
      toast.error("Failed to start survey. Please try again.");
      console.error("Start survey error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResume = () => {
    const savedUser = localStorage.getItem(USER_KEY);
    if (savedUser) {
      sessionStorage.setItem("survey_user", savedUser);
      navigate("/survey");
    }
  };

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = "Please enter your name";
    if (!email.trim()) errs.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "That doesn't look like a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsLoading(true);
    try {
      const userData = { short_name: name.trim(), email: email.trim().toLowerCase() };
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      sessionStorage.setItem("survey_user", JSON.stringify(userData));

      // Save in-progress response to database
      const session = localStorage.getItem(SESSION_KEY);
      let sessionData = {};
      if (session) {
        try { sessionData = JSON.parse(session); } catch {}
      }
      
      await API.post("/survey/init", {
        short_name: userData.short_name,
        email: userData.email,
        session_id: sessionData.session_id || null
      });

      navigate("/survey");
    } catch (e) {
      toast.error("Failed to save your information. Please try again.");
      console.error("Init survey error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdf7ff] relative overflow-hidden" data-testid="landing-page">
      {/* Decorative floating shapes */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-[#f5d0fe]/40 animate-float-slow blur-xl" />
      <div className="absolute top-40 right-20 w-48 h-48 rounded-full bg-[#a21caf]/10 animate-float-delayed blur-2xl" />
      <div className="absolute bottom-20 left-1/4 w-24 h-24 rounded-full bg-[#f59e0b]/15 animate-float-slow blur-lg" />
      <div className="absolute bottom-40 right-1/3 w-36 h-36 rounded-full bg-[#22c55e]/10 animate-float-delayed blur-xl" />

      <div className="relative z-10 flex items-start justify-center min-h-screen p-4 pt-6">
        <div className="w-full max-w-xl animate-slide-up">
          {/* Logo + Org */}
          <div className="flex items-center gap-3 mb-4 justify-center">
            <img 
              src="/LogoAMP.png" 
              alt="AMP Singapore Logo" 
              className="h-20 w-auto"
            />
          </div>

          {screen === "intro" && (
            <div key="intro">
              {/* Hero card */}
              <div className="nb-card mb-6 relative overflow-hidden" data-testid="hero-card">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#f5d0fe]/50 to-transparent rounded-bl-full" />
                <div className="relative">
                  <h1 className="font-parkinsans text-4xl sm:text-5xl text-[#111] mb-3 leading-tight">
                    AMP AI READY
                  </h1>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-5">
                    A quick, friendly check-in on how our team relates to AI today.
                  </p>

                  {/* Stats pills */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    <div className="inline-flex items-center gap-2 bg-[#f5d0fe]/40 px-4 py-2 rounded-xl border-2 border-[#111] shadow-[2px_2px_0px_0px_rgba(17,17,17,1)]">
                      <MessageCircle className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
                      <span className="text-sm font-bold">~19 questions</span>
                    </div>
                    <div className="inline-flex items-center gap-2 bg-[#fef3c7]/60 px-4 py-2 rounded-xl border-2 border-[#111] shadow-[2px_2px_0px_0px_rgba(17,17,17,1)]">
                      <Clock className="w-4 h-4 text-[#f59e0b]" strokeWidth={2.5} />
                      <span className="text-sm font-bold">5-8 min</span>
                    </div>
                    <div className="inline-flex items-center gap-2 bg-[#dcfce7]/60 px-4 py-2 rounded-xl border-2 border-[#111] shadow-[2px_2px_0px_0px_rgba(17,17,17,1)]">
                      <Sparkles className="w-4 h-4 text-[#22c55e]" strokeWidth={2.5} />
                      <span className="text-sm font-bold">Get your AI persona</span>
                    </div>
                  </div>

                  {/* CTA Buttons inside hero card */}
                  {SURVEY_CLOSED ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center gap-1.5 bg-[#111] text-white text-xs font-bold px-3 py-1 rounded-full">
                          <Lock className="w-3 h-3" strokeWidth={2.5} /> SURVEY CLOSED
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        Thank you everyone that has responded.
                      </p>
                      <button
                        disabled
                        className="nb-btn-primary w-full text-lg opacity-40 cursor-not-allowed"
                        data-testid="start-survey-btn"
                      >
                        <Lock className="w-5 h-5" strokeWidth={2.5} /> Survey closed
                      </button>
                    </div>
                  ) : hasSavedProgress ? (
                    <div className="space-y-3">
                      <button
                        onClick={handleResume}
                        disabled={isLoading}
                        className="nb-btn-primary w-full text-lg"
                        data-testid="resume-survey-btn"
                      >
                        <Sparkles className="w-5 h-5" strokeWidth={2.5} /> Resume where you left off
                      </button>
                      <button
                        onClick={handleStartClick}
                        disabled={isLoading}
                        className="nb-btn-secondary w-full text-base"
                        data-testid="start-survey-btn"
                      >
                        {isLoading ? "Starting..." : "Start fresh survey"}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleStartClick}
                      disabled={isLoading}
                      className="nb-btn-primary w-full text-lg"
                      data-testid="start-survey-btn"
                    >
                      {isLoading ? "Starting..." : "Start survey"} <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </div>

              {/* Disclaimer */}
              <div className="nb-card-sm mb-6 bg-[#f5d0fe]/20 border-[#a21caf]/30" data-testid="disclaimer-panel">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-[#a21caf] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <div>
                    <p className="text-sm text-[#111] leading-relaxed">
                      There are no right or wrong answers. Your responses help us understand awareness, familiarity, and concerns around AI — so we can better support and educate where it's needed. This is not a test and has no impact on appraisals, reviews, or evaluations.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-center text-xs text-gray-400">
                Built with love by AMP's Tech Team and <a href="https://10kb.co" target="_blank" rel="noopener noreferrer" className="text-[#a21caf] hover:underline">10KB</a>
              </p>
            </div>
          )}

          {screen === "info" && (
            <div key="info" className="animate-slide-up">
              <div className="nb-card" data-testid="info-form">
                <h2 className="font-parkinsans text-2xl mb-1">Before we begin</h2>
                <p className="text-sm text-gray-500 mb-5">Just two quick things so we know who's sharing.</p>
                <form onSubmit={handleContinue} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1.5" htmlFor="name">Your name</label>
                    <input
                      id="name"
                      type="text"
                      placeholder="First name or nickname"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`nb-input ${errors.name ? "!border-[#ef4444] ring-2 ring-red-200" : ""}`}
                      autoFocus
                      data-testid="input-name"
                    />
                    {errors.name && <p className="text-[#ef4444] text-sm mt-1" data-testid="error-name">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1.5" htmlFor="email">Your email</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Your AMP email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`nb-input ${errors.email ? "!border-[#ef4444] ring-2 ring-red-200" : ""}`}
                      data-testid="input-email"
                    />
                    {errors.email && <p className="text-[#ef4444] text-sm mt-1" data-testid="error-email">{errors.email}</p>}
                  </div>
                  <button 
                    type="submit" 
                    disabled={isLoading}
                    className="nb-btn-primary w-full text-lg disabled:opacity-50" 
                    data-testid="continue-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Saving...</>
                    ) : (
                      <>Continue <ArrowRight className="w-5 h-5" strokeWidth={2.5} /></>
                    )}
                  </button>
                </form>
              </div>
              <button
                onClick={() => setScreen("intro")}
                className="mt-4 text-sm text-gray-400 hover:text-[#a21caf] transition-colors mx-auto block"
                data-testid="back-to-intro-btn"
              >
                &larr; Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
